import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import {
  clearRememberedEmail,
  readRememberedEmail,
  writeRememberedEmail,
} from '@core/auth/login-remember.storage';
import { SessionUser } from '@core/auth/auth-session.model';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { LocationContextService } from '@core/tenant/location-context.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { AuthGoogleSigninButtonComponent } from '@features/auth/presentation/components/auth-google-signin-button/auth-google-signin-button.component';
import { AuthShowcaseAsideComponent } from '@features/auth/presentation/components/auth-showcase-aside/auth-showcase-aside.component';
import { AuthScrollOnFocusDirective } from '@features/auth/presentation/utils/auth-scroll-on-focus.directive';
import { AuthService } from '@features/auth/services/auth.service';
import {
  authErrorI18nKey,
  resolveAuthRequestErrorKind,
} from '@features/auth/utils/auth-request-error.util';
import { prepareGoogleSignInUi } from '@features/auth/utils/google-auth-ready.util';
import {
  clearCreateBusinessIntent,
  hasCreateBusinessIntent,
  setCreateBusinessIntent,
} from '@core/auth/create-business-intent.storage';
import { navigateAfterStaffAuth } from '@features/auth/utils/navigate-after-staff-auth.util';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';

/** Patrón estricto tipo correo: local@dominio.tld (sin espacios). */
const EMAIL_FORMAT_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

@Component({
  selector: 'app-side-login',
  imports: [
    RouterModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AuthGoogleSigninButtonComponent,
    AuthShowcaseAsideComponent,
    AuthScrollOnFocusDirective,
    TablerIconsModule,
  ],
  templateUrl: './side-login.component.html',
})
export class AppSideLoginComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly socialAuthService = inject(SocialAuthService);

  private readonly rememberedEmailOnLoad = readRememberedEmail();
  private authSub?: Subscription;

  @ViewChild('emailInput') private emailInput?: ElementRef<HTMLInputElement>;

  /** Evita renderizar el botón de Google hasta limpiar sesión social previa. */
  showGoogleSignIn = false;
  /** GIS aún no listo o signOut social en curso. */
  googleAuthInitializing = true;
  hidePassword = true;
  isSubmitting = false;
  /** Paso Treinta: hub (Google + CTA correo) o formulario de credenciales. */
  loginStep: 'hub' | 'credentials' = 'hub';
  errorMessage = '';
  /** Visible si falla el login con Google; email opcional para query en registro. */
  googleLoginError = '';
  googlePrefillEmail = '';
  showCreateBusinessHint = false;
  /** 401 / sesión caducada: banner encima del formulario de login. */
  sessionExpired = false;

  /** Muestra errores tras intento de envío o si el usuario editó el campo (no al solo hacer blur). */
  validationAttempted = false;

  form = new FormGroup({
    uname: new FormControl(this.rememberedEmailOnLoad ?? '', [
      Validators.required,
      Validators.pattern(EMAIL_FORMAT_PATTERN),
    ]),
    password: new FormControl('', [Validators.required]),
    rememberDevice: new FormControl(!!this.rememberedEmailOnLoad, { nonNullable: true }),
  });

  get f() {
    return this.form.controls;
  }

  openCredentialsStep(): void {
    this.loginStep = 'credentials';
    this.validationAttempted = false;
    this.errorMessage = '';
    queueMicrotask(() => {
      const el = this.emailInput?.nativeElement;
      if (!el) {
        return;
      }
      el.focus({ preventScroll: true });
      try {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch {
        /* ignore */
      }
    });
  }

  backToHub(): void {
    this.loginStep = 'hub';
    this.validationAttempted = false;
    this.errorMessage = '';
  }

  ngOnInit(): void {
    const emailParam = this.route.snapshot.queryParamMap.get('email')?.trim() ?? '';
    const intentParam = this.route.snapshot.queryParamMap.get('intent')?.trim() ?? '';
    this.sessionExpired =
      this.route.snapshot.queryParamMap.get('reason') === 'session-expired';
    // Entrar limpio: no arrastrar intent de una visita previa a /register.
    // Solo ?intent=create_business (p. ej. “Ya tengo cuenta” desde registro) lo conserva.
    if (intentParam === 'create_business') {
      setCreateBusinessIntent();
    } else {
      clearCreateBusinessIntent();
    }
    this.showCreateBusinessHint = hasCreateBusinessIntent();
    if (emailParam) {
      this.form.patchValue({ uname: emailParam });
      this.loginStep = 'credentials';
    }

    void this.initGoogleAuth();
  }

  private async initGoogleAuth(): Promise<void> {
    // Esperar GIS + initState antes de montar el botón (evita overlay muerto hasta F5).
    await prepareGoogleSignInUi(this.socialAuthService);

    this.googleAuthInitializing = false;
    this.showGoogleSignIn = true;
    this.authSub = this.socialAuthService.authState
      .pipe(distinctUntilChanged((prev, next) => prev?.idToken === next?.idToken))
      .subscribe((user) => {
        if (!user?.idToken || this.isSubmitting) {
          return;
        }
        const emailFromGoogle =
          typeof user.email === 'string' && user.email.trim().length > 0 ? user.email.trim() : '';
        this.googleLoginError = '';
        this.googlePrefillEmail = '';
        this.isSubmitting = true;
        this.authService.loginWithGoogle(user.idToken).subscribe({
          next: (result) => {
            void this.navigateAfterAuth(result.user).finally(() => {
              this.isSubmitting = false;
            });
          },
          error: () => {
            this.isSubmitting = false;
            this.googlePrefillEmail = emailFromGoogle;
            this.googleLoginError = this.translate.instant('auth.login.googleLoginFailedHint');
            this.errorMessage = '';
          },
        });
      });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
  }

  submit() {
    this.validationAttempted = true;
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    const email = (this.form.value.uname ?? '').trim();
    this.form.patchValue({ uname: email }, { emitEvent: false });

    this.errorMessage = '';
    this.googleLoginError = '';
    this.googlePrefillEmail = '';
    this.isSubmitting = true;
    this.authService
      .login({
        email,
        password: this.form.value.password ?? '',
      })
      .subscribe({
        next: (result) => {
          if (this.form.value.rememberDevice) {
            writeRememberedEmail(email);
          } else {
            clearRememberedEmail();
          }
          // Mantener spinner hasta completar navegación (antes se apagaba al responder el API).
          void this.navigateAfterAuth(result.user).finally(() => {
            this.isSubmitting = false;
          });
        },
        error: (err: unknown) => {
          this.isSubmitting = false;
          const kind = resolveAuthRequestErrorKind(err);
          this.errorMessage = this.translate.instant(authErrorI18nKey('auth.login', kind));
          this.form.controls.password.reset('', { emitEvent: false });
        },
      });
  }

  private navigateAfterAuth(user: SessionUser): Promise<boolean> {
    // Alta de negocio propio gana sobre returnUrl (evita meter al tenant ajeno).
    if (hasCreateBusinessIntent()) {
      return navigateAfterStaffAuth({
        user,
        router: this.router,
        tenantContext: this.tenantContext,
        locationContext: this.locationContext,
        adminShellMode: this.adminShellMode,
      });
    }

    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      return this.router.navigateByUrl(returnUrl, { replaceUrl: true });
    }

    return navigateAfterStaffAuth({
      user,
      router: this.router,
      tenantContext: this.tenantContext,
      locationContext: this.locationContext,
      adminShellMode: this.adminShellMode,
    });
  }
}

